/**
 * Manager "Team" roster.
 *
 * Closes the supervision gap where a manager had no way to see which clients
 * each agent carries. The client directory only shows the assigned agent to
 * super_admins, and `/manager/clients` doesn't return the agent at all.
 *
 * Scope: read-only. Lists active agents (book size + in-flight onboarding
 * count from GET /manager/agents) and lazy-loads each agent's roster on expand
 * via GET /agents/{id}/clients — both endpoints already exist; RBAC already
 * lets a manager target any agent. Each client deep-links to their profile.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import managerService, {
  type ManagerAgent,
  type ClientSummary,
} from '../../services/manager.service';
import agentService from '../../services/agent.service';
import UnassignedPool from '../../components/manager/UnassignedPool';
import ReassignAgentModal from '../../components/manager/ReassignAgentModal';
import TimeOffManager from '../../components/scheduling/TimeOffManager';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import { filterAgents, clientCountLabel } from './team.helpers';

function Badge({ label, tone }: Readonly<{ label: string; tone: 'navy' | 'muted' }>): JSX.Element {
  const bg = tone === 'navy' ? colors.navy : colors.surface;
  const fg = tone === 'navy' ? '#FFFFFF' : colors.muted;
  const border = tone === 'navy' ? colors.navy : colors.border;
  return (
    <span
      className="flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ background: bg, color: fg, border: `1px solid ${border}` }}
    >
      {label}
    </span>
  );
}

export default function Team(): JSX.Element {
  const [agents, setAgents] = useState<ManagerAgent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // Team time-off (#66): the TimeOffManager modal (mode="manager") pre-selected
  // to the agent whose row launched it.
  const [timeOffAgent, setTimeOffAgent] = useState<{ id: string; name: string } | null>(null);
  // Per-client reassign / return-to-pool, launched from a roster row. agentId is
  // the agent whose roster the client sits in (the modal's "current" agent).
  const [reassignClient, setReassignClient] =
    useState<{ id: string; name: string; agentId: string } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Single-open accordion. Rosters are lazy-loaded on expand and refetched on
  // each expand so a reassignment made elsewhere shows up without a reload.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rosters, setRosters] = useState<Record<string, ClientSummary[]>>({});
  const [rosterLoading, setRosterLoading] = useState<Record<string, boolean>>({});
  const [rosterError, setRosterError] = useState<Record<string, string | null>>({});

  const loadAgents = useCallback((): void => {
    managerService
      .listAgents()
      .then((items) => setAgents(items))
      .catch((err: unknown) => setLoadError(getApiErrorMessage(err, 'Could not load the team.')));
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const filtered = useMemo(() => filterAgents(agents ?? [], query), [agents, query]);
  const agentCount = agents?.length ?? 0;
  const agentsWord = agentCount === 1 ? 'agent' : 'agents';

  const loadRoster = useCallback((agentId: string): void => {
    setRosterLoading((m) => ({ ...m, [agentId]: true }));
    setRosterError((m) => ({ ...m, [agentId]: null }));
    agentService
      .listClientsForAgent(agentId)
      .then((items) => {
        setRosters((m) => ({ ...m, [agentId]: items }));
      })
      .catch((err: unknown) => {
        setRosterError((m) => ({
          ...m,
          [agentId]: getApiErrorMessage(err, 'Could not load this roster.'),
        }));
      })
      .finally(() => {
        setRosterLoading((m) => ({ ...m, [agentId]: false }));
      });
  }, []);

  const toggle = (agentId: string): void => {
    const next = expandedId === agentId ? null : agentId;
    setExpandedId(next);
    // Refetch every time the agent is opened (not just the first) so a
    // reassignment made on another screen is reflected. The previously
    // loaded roster stays visible while the refetch runs (see renderRoster).
    if (next && !rosterLoading[agentId]) {
      loadRoster(agentId);
    }
  };

  const renderRoster = (agentId: string): JSX.Element => {
    // Loader only on first load; on a refetch keep the cached roster visible
    // to avoid a flicker each time the agent is re-opened.
    if (rosterLoading[agentId] && rosters[agentId] === undefined) {
      return <p className="text-sm py-2" style={{ color: colors.muted }}>Loading roster…</p>;
    }
    if (rosterError[agentId]) {
      return (
        <p className="text-sm py-2" style={{ color: colors.danger }} role="alert">
          {rosterError[agentId]}
        </p>
      );
    }
    const roster = rosters[agentId] ?? [];
    if (roster.length === 0) {
      return <p className="text-sm py-2" style={{ color: colors.muted }}>No clients assigned.</p>;
    }
    return (
      <ul className="space-y-1.5 pt-1">
        {roster.map((c) => (
          <li
            key={c.id}
            className="rounded-lg px-3 py-2 flex items-center justify-between gap-3"
            style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" style={{ color: colors.ink }}>
                {fullName({ first_name: c.first_name, last_name: c.last_name })}
              </p>
              <span className="text-xs font-mono truncate block" style={{ color: colors.muted }}>
                {c.email}
              </span>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setReassignClient({
                    id: c.id,
                    name: fullName({ first_name: c.first_name, last_name: c.last_name }),
                    agentId,
                  })
                }
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: colors.surface, color: colors.navy, border: `1px solid ${colors.border}` }}
              >
                Reassign…
              </button>
              <Link
                to={`/manager/clients/${c.id}`}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: colors.navy, color: '#FFFFFF' }}
              >
                Open profile →
              </Link>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Your agents and the clients each one carries. Expand an agent to see their roster."
      />

      {loadError && (
        <div
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
          role="alert"
        >
          {loadError}
        </div>
      )}

      {flash && (
        <div
          className="rounded-lg p-3 mb-4 text-sm"
          style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}
          role="status"
          aria-live="polite"
        >
          {flash}
        </div>
      )}

      <UnassignedPool agents={agents ?? []} onReassigned={loadAgents} />

      <div className="mb-4">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search agents by name or email…"
          className="w-full max-w-md px-3 py-2 rounded-lg text-sm"
          style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}
        />
      </div>

      {agents === null && !loadError && (
        <div className="rounded-2xl p-6" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <p style={{ color: colors.muted }}>Loading…</p>
        </div>
      )}

      {agents !== null && agents.length === 0 && (
        <div className="rounded-2xl p-6" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <p style={{ color: colors.muted }}>There are no active agents yet.</p>
        </div>
      )}

      {agents !== null && agents.length > 0 && filtered.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>No agents match &ldquo;{query}&rdquo;.</p>
      )}

      {filtered.length > 0 && (
        <ul className="space-y-2">
          {filtered.map((a) => {
            const isOpen = expandedId === a.id;
            return (
              <li
                key={a.id}
                className="rounded-xl overflow-hidden"
                style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
              >
                <button
                  type="button"
                  onClick={() => toggle(a.id)}
                  aria-expanded={isOpen}
                  className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate" style={{ color: colors.ink }}>
                      {fullName({ first_name: a.first_name, last_name: a.last_name })}
                    </p>
                    <span className="text-xs font-mono truncate block" style={{ color: colors.muted }}>
                      {a.email}
                    </span>
                  </div>
                  <Badge label={clientCountLabel(a.client_count)} tone="navy" />
                  {a.pending_count > 0 && (
                    <Badge label={`${a.pending_count} in review`} tone="muted" />
                  )}
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 text-xs transition-transform"
                    style={{ color: colors.muted, transform: isOpen ? 'rotate(90deg)' : 'none' }}
                  >
                    ▶
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3" style={{ borderTop: `1px solid ${colors.border}` }}>
                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={() => setTimeOffAgent({ id: a.id, name: fullName(a) })}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ background: colors.surface, color: colors.navy, border: `1px solid ${colors.border}` }}
                      >
                        Manage time-off ↗
                      </button>
                    </div>
                    {renderRoster(a.id)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {agents !== null && filtered.length > 0 && (
        <p className="text-xs mt-4" style={{ color: colors.mutedSoft }} aria-live="polite">
          {query ? `${filtered.length} of ${agentCount} match` : `${agentCount} ${agentsWord}`}
        </p>
      )}

      {timeOffAgent && (
        <TimeOffManager
          mode="manager"
          initialAgentId={timeOffAgent.id}
          initialAgentName={timeOffAgent.name}
          onClose={() => setTimeOffAgent(null)}
        />
      )}

      {reassignClient && (
        <ReassignAgentModal
          clientId={reassignClient.id}
          clientName={reassignClient.name}
          currentAgentId={reassignClient.agentId}
          onClose={() => setReassignClient(null)}
          onReassigned={({ newAgentName, reassignedCases }) => {
            const { agentId, name } = reassignClient;
            setFlash(
              `Moved ${name} to ${newAgentName}. ${reassignedCases} active case${
                reassignedCases === 1 ? '' : 's'
              } updated.`,
            );
            setReassignClient(null);
            loadRoster(agentId); // client leaves this agent's roster
            loadAgents(); // refresh client counts
            globalThis.setTimeout(() => setFlash(null), 6000);
          }}
        />
      )}
    </>
  );
}
